import React from 'react'
import { useTheme } from '@mui/material/styles'
import { DateTimePicker, type DateTimePickerProps } from '@mui/x-date-pickers/DateTimePicker'

import { FormHelperText } from '~components/common'

// Custom interface for TextField slot props that includes error and helperText
interface CustomTextFieldSlotProps {
  error?: boolean
  helperText?: string
  id?: string
}

type CustomDateTimePickerProps = Omit<DateTimePickerProps, 'open' | 'onOpen' | 'onClose'>

const CustomDateTimePicker: React.FC<CustomDateTimePickerProps> = (props) => {
  const [open, setOpen] = React.useState(false)
  const theme = useTheme()

  // Extract error states from both sources
  const textFieldSlotProps = props.slotProps?.textField as CustomTextFieldSlotProps | undefined
  const hasError = textFieldSlotProps?.error
  const helperText = textFieldSlotProps?.helperText

  const handleClose = (): void => {
    const activeElement = document.activeElement
    const isInputElement = activeElement?.tagName === 'INPUT'

    if (!isInputElement) {
      setOpen(false)
    }
  }

  const placeHolder = props.disabled ? { placeholder: '' } : {}

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <DateTimePicker
        {...props}
        open={open}
        onClose={handleClose}
        slotProps={{
          ...props.slotProps,
          textField: {
            ...props.slotProps?.textField,
            onClick: () => {
              if (!props.disabled) setOpen(!open)
            },
            ...placeHolder,
            slotProps: {
              formHelperText: {
                sx: {
                  display: 'none', // Hide the default helper text
                },
              },
            },
            sx: {
              backgroundColor: props.disabled ? theme.palette.action.disabledInput : 'white',
              '& .MuiInputBase-root': {
                cursor: 'pointer',
              },
              '& .MuiInputBase-input': {
                cursor: 'text',
              },
              '& .MuiInputBase-input::placeholder': {
                color: theme.palette.text.secondary,
                opacity: 1,
              },
              '& .MuiOutlinedInput-notchedOutline': !props.label
                ? {
                    border: 'none',
                    boxShadow: props.disabled
                      ? `inset 0 0 0 1px ${theme.palette.input.disabled}`
                      : hasError
                        ? `inset 0 0 0 2px ${theme.palette.error.main}`
                        : `inset 0 0 0 1px ${theme.palette.input.default}`,
                    transition: theme.transitions.create(['box-shadow']),
                  }
                : {
                    border: `1px solid ${theme.palette.input.default}`,
                  },
              '& .MuiOutlinedInput-root:not(.Mui-disabled):hover:not(.Mui-focused) .MuiOutlinedInput-notchedOutline':
                !props.label
                  ? {
                      boxShadow: `inset 0 0 0 2px ${theme.palette.input.hover}`,
                    }
                  : { border: `2px solid ${theme.palette.input.hover}` },
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': !props.label
                ? {
                    boxShadow: `inset 0 0 0 2px ${theme.palette.input.hover}, 0 0 0 2px ${theme.palette.input.focus}`,
                    outline: 'none',
                  }
                : { border: `2px solid ${theme.palette.input.hover}` },
              '& .MuiOutlinedInput-root.Mui-focused:hover .MuiOutlinedInput-notchedOutline':
                !props.label
                  ? {
                      boxShadow: `inset 0 0 0 2px ${theme.palette.input.hover}, 0 0 0 2px ${theme.palette.input.focus}`,
                      outline: 'none',
                    }
                  : { border: `2px solid ${theme.palette.input.hover}` },
            },
          },
          openPickerButton: {
            ...props.slotProps?.openPickerButton,
            onClick: () => {
              if (!props.disabled) setOpen(!open)
            },
          },
          popper: {
            ...props.slotProps?.popper,
            sx: {
              zIndex: 1300,
            },
          },
        }}
      />
      {helperText && (
        <FormHelperText
          id={textFieldSlotProps?.id}
          aria-describedby={textFieldSlotProps?.id}
          error={hasError}
          isVisible={hasError || !!helperText}
        >
          {helperText}
        </FormHelperText>
      )}
    </div>
  )
}

export default CustomDateTimePicker
